import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { nutztFunktion } from "@/office/excel/formeln";
import { formulaCells, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionGrundfunktionen: Lesson = {
  id: "grundfunktionen",
  title: "Die fünf Funktionen für den Anfang",
  summary: "SUMME, MITTELWERT, ANZAHL, MIN und MAX decken den größten Teil des Alltags ab.",
  durationMinutes: 11,
  learningGoals: [
    "Den Aufbau jeder Excel-Funktion erklären",
    "SUMME und die weiteren Grundfunktionen einsetzen",
    "Den Unterschied zwischen ANZAHL und ANZAHL2 kennen",
  ],
  steps: [
    {
      id: "aufbau",
      title: "Wie eine Funktion aufgebaut ist",
      explanation: [
        "Jede Funktion folgt demselben Bauplan: ein Name, eine Klammer, darin die " +
          "Argumente, dann die Klammer zu. =SUMME(B2:B20) heißt: Funktion SUMME, ein " +
          "Argument, nämlich der Bereich B2:B20. Mehrere Argumente werden getrennt – im " +
          "deutschen Excel mit einem Semikolon, im englischen mit einem Komma.",
        "Die Namen sind sprachabhängig. Ein deutsches Excel kennt SUMME, ein englisches " +
          "SUM, und beim Öffnen einer Datei übersetzt Excel automatisch. Was gespeichert " +
          "wird, ist ohnehin ein sprachneutraler Code – deshalb funktioniert dieselbe " +
          "Datei überall, auch wenn sie anders aussieht.",
        "Beim Tippen hilft Excel mit: Nach den ersten Buchstaben erscheint eine Liste " +
          "passender Funktionen, die Tabulatortaste übernimmt die markierte. Sobald die " +
          "Klammer offen ist, zeigt ein QuickInfo die erwarteten Argumente an, das " +
          "gerade bearbeitete jeweils fett. Wer das liest, braucht die Hilfe selten.",
        "Argumente in eckigen Klammern im QuickInfo sind optional. Bei =RUNDEN(Zahl; " +
          "Anzahl_Stellen) ist beides Pflicht, bei =ANZAHL(Wert1; [Wert2]; …) nur das " +
          "erste. Diese Notation kommt in der ganzen Excel-Hilfe vor.",
      ],
      keyPoints: [
        "Aufbau: NAME(Argument1;Argument2). Semikolon trennt im deutschen Excel.",
        "Eckige Klammern im QuickInfo kennzeichnen optionale Argumente.",
        "Alt + = fügt eine SUMME über den darüberliegenden Bereich ein.",
      ],
    },
    {
      id: "summe",
      title: "SUMME – und der Trick mit Alt + =",
      explanation: [
        "SUMME addiert alles, was in ihrem Bereich steht. Texte und leere Zellen " +
          "überspringt sie stillschweigend, was bequem ist und gleichzeitig die Falle " +
          "aus Modul 2 verbirgt: Eine als Text gespeicherte Zahl wird nicht mitgezählt, " +
          "ohne dass Excel sich beschwert. Eine Summe, die zu niedrig aussieht, ist " +
          "fast immer dieser Fall.",
        "Statt den Bereich zu markieren, geht es meist schneller mit Alt und " +
          "Gleichheitszeichen: Excel schlägt selbst den Bereich oberhalb der aktuellen " +
          "Zelle vor. Ein Blick auf den vorgeschlagenen Bereich lohnt trotzdem – bei " +
          "einer Leerzeile in den Daten hört der Vorschlag genau dort auf.",
        "Ein Bereich in einer Formel darf auch die ganze Spalte sein: =SUMME(B:B). Das " +
          "ist praktisch, wenn ständig Zeilen dazukommen, hat aber einen Haken – die " +
          "Summe darf dann nicht selbst in Spalte B stehen, sonst entsteht ein " +
          "Zirkelbezug. Die saubere Lösung dafür kommt im Modul über Tabellen.",
      ],
      keyPoints: [
        "Alt + = fügt eine SUMME mit vorgeschlagenem Bereich ein.",
        "SUMME überspringt Text kommentarlos – eine zu niedrige Summe hat oft diese Ursache.",
        "In der Statusleiste unten zeigt Excel Summe und Mittelwert der Markierung an.",
      ],
      task: {
        instruction:
          "Bilde mit SUMME die Summe einer deiner Zahlenspalten.",
        hint: "Cursor unter die Spalte setzen und Alt + = drücken. Prüfe den vorgeschlagenen Bereich, bevor du bestätigst.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort SUMME mit einem Bereich steht.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          const summen = formeln.filter((zelle) => nutztFunktion(zelle.formula, "summe"));

          if (summen.length === 0) {
            return fail(
              formeln.length === 0
                ? "Es steht noch keine Formel in der Arbeitsmappe."
                : `Von ${formeln.length} Formeln benutzt keine die Funktion SUMME.`,
              "Setze den Cursor unter deine Zahlenspalte und drücke Alt + Gleichheitszeichen.",
            );
          }

          const erste = summen[0];
          return pass(
            `In ${erste.address} steht „${erste.formula}“ und liefert „${erste.text}“.` +
              (summen.length > 1 ? ` Insgesamt ${summen.length} Summen in der Arbeitsmappe.` : ""),
          );
        },
      },
    },
    {
      id: "weitere-grundfunktionen",
      title: "MITTELWERT, ANZAHL, MIN und MAX",
      explanation: [
        "MITTELWERT rechnet den Durchschnitt – und zwar über die Zellen, in denen etwas " +
          "steht. Eine leere Zelle senkt den Durchschnitt also nicht, eine Zelle mit " +
          "einer 0 dagegen schon. Wer diesen Unterschied nicht kennt, wundert sich über " +
          "Durchschnittswerte, die zu hoch wirken.",
        "ANZAHL und ANZAHL2 sind das klassische Verwechslungspaar. ANZAHL zählt nur " +
          "Zellen mit Zahlen, ANZAHL2 zählt alle nicht leeren – auch die mit Text. Um " +
          "die Zeilen einer Namensliste zu zählen, braucht man also ANZAHL2; ANZAHL " +
          "liefert dort 0. Umgekehrt ist ANZAHL das richtige Werkzeug, um zu prüfen, " +
          "wie viele Werte einer Zahlenspalte tatsächlich ausgefüllt sind.",
        "MIN und MAX liefern den kleinsten und größten Wert eines Bereichs. In " +
          "Kombination sind sie ein schneller Plausibilitätstest: Zeigt MIN einer " +
          "Preisspalte eine 0 oder MAX eine unrealistische Zahl, steckt in den Daten ein " +
          "Fehler – meist ein Tippfehler mit einer Null zu viel.",
      ],
      keyPoints: [
        "ANZAHL zählt Zahlen, ANZAHL2 zählt alles Nichtleere.",
        "MITTELWERT überspringt leere Zellen, nicht aber Nullen.",
        "MIN und MAX über eine Spalte finden Ausreißer in Sekunden.",
      ],
      task: {
        instruction:
          "Ergänze zu deiner Summe mindestens zwei weitere Auswertungen mit " +
          "MITTELWERT, ANZAHL, ANZAHL2, MIN oder MAX.",
        hint: "Schreibe die Bezeichnung in die Zelle links daneben, damit man später weiß, was da steht.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh nach, ob unter deiner Liste mehrere Auswertungen mit verschiedenen Funktionen stehen.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          const gesucht = ["mittelwert", "anzahl", "anzahl2", "min", "max"] as const;
          const gefunden = gesucht.filter((name) =>
            formeln.some((zelle) => nutztFunktion(zelle.formula, name)),
          );

          return fromCriteria(
            [
              criterion(
                `Eine Summe ist vorhanden`,
                formeln.some((zelle) => nutztFunktion(zelle.formula, "summe")),
              ),
              criterion(
                gefunden.length > 0
                  ? `Weitere Funktionen: ${gefunden.map((name) => name.toUpperCase()).join(", ")}`
                  : "Noch keine der Funktionen MITTELWERT, ANZAHL, ANZAHL2, MIN oder MAX",
                gefunden.length >= 2,
              ),
            ],
            {
              success:
                `Deine Auswertung nutzt SUMME und ${gefunden.map((name) => name.toUpperCase()).join(", ")} – ` +
                "damit lässt sich eine Zahlenspalte schon recht gut beschreiben.",
              failure: "Es fehlen noch Auswertungen.",
              hint: "Zwei verschiedene Funktionen sind gefragt, zum Beispiel =MITTELWERT(B2:B20) und =MAX(B2:B20).",
            },
          );
        },
      },
    },
  ],
};
